/**
 * Quick Actions de WhatsApp durante llamada activa (Voice Commerce M4).
 *
 * 3 botones contextuales:
 *   - Enviar ubicación de tienda (Google Maps link)
 *   - Enviar foto de producto (picker + envía media + caption)
 *   - Enviar mensaje custom
 *
 * Reusa useProductSearch del M3 para el picker.
 *
 * Plan: docs/plan/PLAN-VOICE-COMMERCE-M4-WA-QUICK-ACTIONS.md
 */

import { useEffect, useState } from 'react';
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@/lib/polaris';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { useAgentWaQuickActions } from '@/hooks/useAgentWaQuickActions';
import { useProductSearch, type QuickProduct } from '@/hooks/useAgentQuickOrder';

interface AgentWaQuickActionsProps {
  tenantId: string;
  customerPhone: string | null;
  customerName?: string | null;
  crmCallId?: string | null;
  compact?: boolean;
}

const DEBOUNCE_MS = 300;
type Mode = 'idle' | 'product' | 'text';

export function AgentWaQuickActions({
  tenantId,
  customerPhone,
  customerName,
  crmCallId,
  compact = false,
}: AgentWaQuickActionsProps) {
  const toast = useAdminToast();
  const mutation = useAgentWaQuickActions();
  const [mode, setMode] = useState<Mode>('idle');

  // Product picker state
  const [productSearch, setProductSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(productSearch), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [productSearch]);
  const search = useProductSearch(debouncedSearch, { tenantId });

  // Custom text state
  const [customText, setCustomText] = useState('');

  const phoneIsValid = !!customerPhone && /^\+[1-9][0-9]{6,14}$/.test(customerPhone);

  const baseInput = {
    customer_phone: customerPhone ?? '',
    customer_name: customerName ?? undefined,
    crm_call_id: crmCallId ?? undefined,
  };

  const handleSendLocation = async () => {
    if (!phoneIsValid) return;
    try {
      const res = await mutation.mutateAsync({ action: 'send_location', ...baseInput });
      if (res.ok) toast.success('Ubicación enviada por WhatsApp');
      else toast.error(`No se pudo enviar: ${(res.errors ?? ['unknown']).join(', ')}`);
    } catch (e) {
      toast.error(`Error: ${(e as Error).message}`);
    }
  };

  const handleSendProduct = async (product: QuickProduct) => {
    if (!phoneIsValid) return;
    try {
      const res = await mutation.mutateAsync({
        action: 'send_product',
        product_id: product.id,
        ...baseInput,
      });
      if (res.ok) {
        toast.success(`"${product.name}" enviado por WhatsApp`);
        setMode('idle');
        setProductSearch('');
        setDebouncedSearch('');
      } else {
        toast.error(`No se pudo enviar: ${(res.errors ?? ['unknown']).join(', ')}`);
      }
    } catch (e) {
      toast.error(`Error: ${(e as Error).message}`);
    }
  };

  const handleSendText = async () => {
    const trimmed = customText.trim();
    if (!trimmed || !phoneIsValid) return;
    try {
      const res = await mutation.mutateAsync({ action: 'send_text', text: trimmed, ...baseInput });
      if (res.ok) {
        toast.success('Mensaje enviado por WhatsApp');
        setCustomText('');
        setMode('idle');
      } else {
        toast.error(`No se pudo enviar: ${(res.errors ?? ['unknown']).join(', ')}`);
      }
    } catch (e) {
      toast.error(`Error: ${(e as Error).message}`);
    }
  };

  return (
    <Card>
      <Box padding={compact ? '300' : '400'}>
        <BlockStack gap="300">
          <Text as="h3" variant="headingMd">Enviar por WhatsApp</Text>

          {!phoneIsValid && (
            <Banner tone="warning">
              <Text as="p">Sin número en E.164 no se puede enviar por WhatsApp.</Text>
            </Banner>
          )}

          {/* Modo idle: 3 botones */}
          {mode === 'idle' && (
            <InlineStack gap="200" wrap>
              <Button
                onClick={handleSendLocation}
                disabled={!phoneIsValid || mutation.isPending}
                loading={mutation.isPending && mutation.variables?.action === 'send_location'}
              >
                📍 Ubicación de tienda
              </Button>
              <Button
                onClick={() => setMode('product')}
                disabled={!phoneIsValid}
              >
                🛍️ Foto de producto
              </Button>
              <Button
                onClick={() => setMode('text')}
                disabled={!phoneIsValid}
              >
                💬 Mensaje custom
              </Button>
            </InlineStack>
          )}

          {/* Modo product picker */}
          {mode === 'product' && (
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="span" variant="bodyMd" fontWeight="semibold">Elegir producto</Text>
                <Button variant="tertiary" onClick={() => setMode('idle')}>Cancelar</Button>
              </InlineStack>

              <TextField
                label="Buscar producto"
                labelHidden
                placeholder="Buscar producto…"
                value={productSearch}
                onChange={setProductSearch}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => {
                  setProductSearch('');
                  setDebouncedSearch('');
                }}
              />

              {search.isFetching && debouncedSearch.length >= 2 && (
                <InlineStack align="center"><Spinner accessibilityLabel="Buscando" size="small" /></InlineStack>
              )}

              {!search.isFetching && (search.data ?? []).length > 0 && (
                <BlockStack gap="100">
                  {(search.data ?? []).slice(0, 6).map((p) => (
                    <Box
                      key={p.id}
                      borderWidth="025"
                      borderColor="border"
                      borderRadius="200"
                      padding="200"
                    >
                      <InlineStack align="space-between" blockAlign="center" gap="200">
                        <BlockStack gap="050">
                          <Text as="p" variant="bodySm" fontWeight="semibold">{p.name}</Text>
                          <Text as="span" variant="bodySm" tone="subdued">
                            ${Number(p.price).toFixed(2)}
                          </Text>
                        </BlockStack>
                        <Button
                          size="micro"
                          variant="primary"
                          onClick={() => handleSendProduct(p)}
                          loading={mutation.isPending && mutation.variables?.action === 'send_product' && mutation.variables.product_id === p.id}
                        >
                          Enviar
                        </Button>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}

              {!search.isFetching && debouncedSearch.length >= 2 && (search.data ?? []).length === 0 && (
                <Text as="p" tone="subdued" variant="bodySm">Sin resultados para "{debouncedSearch}"</Text>
              )}
            </BlockStack>
          )}

          {/* Modo custom text */}
          {mode === 'text' && (
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="span" variant="bodyMd" fontWeight="semibold">Mensaje custom</Text>
                <Button variant="tertiary" onClick={() => setMode('idle')}>Cancelar</Button>
              </InlineStack>

              <TextField
                label="Mensaje"
                labelHidden
                placeholder="Escribe el mensaje a enviar…"
                value={customText}
                onChange={setCustomText}
                multiline={3}
                autoComplete="off"
                maxLength={1500}
                showCharacterCount
              />

              <InlineStack align="end">
                <Button
                  variant="primary"
                  disabled={!customText.trim() || !phoneIsValid}
                  loading={mutation.isPending && mutation.variables?.action === 'send_text'}
                  onClick={handleSendText}
                >
                  Enviar mensaje
                </Button>
              </InlineStack>
            </BlockStack>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}
